import type {MouseEvent, MouseEventHandler, ReactElement, ReactNode, Ref} from 'react';
import React from 'react';
import './button.scss';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Icon} from '../icon-v2';

export interface ButtonProps {
  type?: Type;
  /**
   * ID to give to the button
   */
  id?: string;
  /**
   * Additional classes to give to the button
   */
  customClasses?: string;
  size?: Size;
  iconPlacement?: IconPlacement;
  href?: string;
  /**
   * The class that renders the icon
   */
  icon?: string;
  iconClassName?: string;
  /**
   * Additional classes to give to the text span too
   */
  textClasses?: string;
  /**
   * Text on button to display
   */
  text?: string;
  title?: string;
  /**
   * Text on button to below 500px width
   */
  shortText?: string;
  /**
   * Handler for when mouse is clicked
   */
  onClick?: MouseEventHandler | (() => Promise<any>);
  disabled?: boolean;
  hasSharpEdges?: boolean;
  /**
   * a key value pair of any attributes we want to give to the root component such as data-id etc
   */
  attributes?: Record<string, string>;
  children?: ReactNode;
  isLoading?: boolean;
  isFullWidth?: boolean;
  /**
   * classes that get applied to the loader
   */
  loaderClassName?: string;
  isActive?: boolean;
  /**
   * Set this to enable click tracking for this button on GA4.
   */
  ga4ClickName?: string;
  customIcon?: ReactElement<any>;
  customText?: ReactElement<any>;
  loadingStateText?: string;
  showPremiumUpsellIcon?: boolean;
  'data-testid'?: string;
  dangerouslySetTextAsHTML?: boolean;
  isSelected?: boolean;
}

export enum Size {
  XSMALL = 'xsmall',
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export enum Type {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  DANGER = 'danger',
  SUCCESS = 'success',
  PREMIUM = 'premium',
  PREMIUM2 = 'premium2',
  PREMIUM3 = 'premium3',
  PRIMARY_DARK = 'primarydark',
  DARK = 'dark',
  DARK_SECONDARY = 'darksecondary',
  GHOST = 'ghost',
  DASHED = 'dashed',
  DARK_GHOST = 'darkghost',
  AI = 'ai'
}

export enum IconPlacement {
  LEFT = '-icon-left',
  RIGHT = '-icon-right',
}

const HREF_DEFAULT = 'javascript:void(0);';

export const Button = React.forwardRef(
  (
    {
      type,
      href = HREF_DEFAULT,
      id = '',
      size = Size.MEDIUM,
      customClasses = '',
      icon = '',
      iconClassName = '',
      loaderClassName = '',
      textClasses = '',
      iconPlacement = IconPlacement.LEFT,
      disabled = false,
      hasSharpEdges = false,
      onClick = (): void => {},
      text = '',
      title = '',
      attributes = {},
      isLoading = false,
      isFullWidth = false,
      isActive = false,
      showPremiumUpsellIcon = false,
      dangerouslySetTextAsHTML = false,
      isSelected = false,
      ...props
    }: ButtonProps,
    ref: Ref<any>
  ): ReactElement<any> | null => {
    const hasUrl = href !== HREF_DEFAULT;
    const BtnTag = hasUrl ? 'a' : 'button';
    const classes = [
      'btn',
      size,
      type,
      type === Type.DASHED ? '-no-border' : '',
      iconPlacement,
      hasSharpEdges ? '-sharp-edges' : '',
      isLoading ? '-loading' : '',
      isFullWidth ? '-fullwidth' : '',
      customClasses,
      props.shortText !== undefined ? '-has-short-text' : '',
      text ? '' : '-no-text',
      isActive ? '-active' : '',
      isSelected ? '-selected' : '',
    ];

    hasUrl ? (attributes.href = href) : null;

    const getIconSize = (): IconSize => {
      switch (size) {
        case Size.XSMALL:
          return IconSize.SIZE_ICON_16;
        case Size.SMALL:
          return IconSize.SIZE_ICON_16;
        case Size.MEDIUM:
          return IconSize.SIZE_ICON_20;
        case Size.LARGE:
          return IconSize.SIZE_ICON_24;
        default:
          return IconSize.SIZE_ICON_20;
      }
    };

    const getLoader = (): ReactElement<any> => {
      return (
        <div className={`${loaderClassName} btn-loader`}>
          <progress className="icon-circular-loader" />
        </div>
      );
    };

    const getLoaderWithLoadingStateText = (): ReactElement<any> => {
      return (
        <div className={`${loaderClassName} _text-transform-none btn-loader`}>
          <progress className="icon-circular-loader" />
          <span className="spacing-m-l-2">{props.loadingStateText}</span>
        </div>
      );
    };

    const onClickHandler = (e: MouseEvent): void => {
      if (isLoading) {
        return;
      }

      if (typeof window.PMW?.gtm !== 'undefined' && props.ga4ClickName !== undefined && props.ga4ClickName !== '') {
        window.PMW.gtm.trackGA4CustomClick(props.ga4ClickName);
      }

      void onClick(e);
    };

    const renderIcon = (): ReactElement<any> | null => {
      if (props.customIcon) {
        return <span className="btn-icon">{props.customIcon}</span>;
      }

      if (icon) {
        return (
          <span className="btn-icon">
            <Icon icon={icon} className={iconClassName} size={getIconSize()} />
          </span>
        );
      }

      return null;
    };

    const getLongText = (): ReactElement<any> => {
      const textClasses = `-long ${props.shortText !== undefined ? '-has-short-text' : ''}`;
      if (dangerouslySetTextAsHTML) {
        return <span className={textClasses} dangerouslySetInnerHTML={{__html: text}}></span>;
      }

      return <span className={textClasses}>{text}</span>;
    };

    const getShortText = (): ReactElement<any> | null => {
      return props.shortText ? <span className="-short">{props.shortText}</span> : null;
    };

    const renderPremiumUpsellIcon = (): ReactElement<any> => {
      return (
        <div className="radius-round bg-neutral-0 premium-icon-container">
          <Icon icon="icon-crown" size={IconSize.SIZE_ICON_16} className="ct-premium-primary-icon" />
        </div>
      );
    };

    const renderCustomText = (): ReactElement<any> | null => {
      if (props.customText) {
        return props.customText;
      }

      return null;
    };

    return (
      <BtnTag ref={ref} id={id} className={classes.join(' ')} disabled={disabled} data-testid={props['data-testid']} onClick={onClickHandler} title={title} {...attributes}>
        <span className={`btn-text ${textClasses}`}>
          {props.customText ? (
            renderCustomText()
          ) : (
            <>
              {getLongText()}
              {getShortText()}
            </>
          )}
        </span>
        {renderIcon()}
        {props.loadingStateText ? getLoaderWithLoadingStateText() : getLoader()}
        {props.children}
        {showPremiumUpsellIcon ? renderPremiumUpsellIcon() : null}
      </BtnTag>
    );
  }
);
